import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  ReactNode,
  MouseEvent,
} from 'react';
import isNil from 'lodash/isNil';
import Popover from 'core/components/Popover';
import Button from 'pages/Dashboard/components/Button';
import NotificationIcon from 'core/icons/notification';
import {
  UserDTO,
} from 'dtos';
import {
  ToDo,
} from 'types/toDo';
import {
  useGetUsers,
} from 'pages/Dashboard/hooks/providers';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  useUpdateToDoCollaborators,
} from 'pages/Dashboard/pages/Todos/hooks';
import CollaboratorsSummary from 'pages/Dashboard/pages/Todos/components/CollaboratorsSummary';
import CollaboratorsAutoComplete from 'pages/Dashboard/pages/Todos/components/CollaboratorsAutoComplete';

function getSelectableToDoUsers(users?: UserDTO[]) {
  return users?.filter((user) => user?.isActive && !user?.isServiceAccount) ?? [];
}

function getUserIds(users: UserDTO[]) {
  return users
    .map((user) => user.userId)
    .filter((userId): userId is number => !isNil(userId));
}

type Props = {
  toDo?: ToDo;
  showAddButton?: boolean;
  emptyState?: ReactNode;
  max?: number;
  className?: string;
};

export default function EditableCollaboratorsSummary({
  toDo,
  showAddButton = true,
  emptyState = null,
  max = 3,
  className,
}: Props) {
  const { data: users } = useGetUsers({ onlyDoctors: false });
  const userInfo = useUserInfo();
  const activeUsers = useMemo(
    () => getSelectableToDoUsers(users),
    [users],
  );
  const collaborators = toDo?.collaboratorUsers ?? [];
  const currentUserId = userInfo?.user?.userId;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [value, setValue] = useState<UserDTO[]>(collaborators);
  const {
    asyncHandler: updateCollaborators,
    isLoading: isUpdating,
  } = useUpdateToDoCollaborators({ successMessage: 'Collaborators updated successfully!' });

  useEffect(() => {
    setValue(collaborators);
  }, [collaborators]);

  const handleOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setValue(collaborators);
    setAnchorEl(null);
  }, [collaborators]);

  const handleChange = useCallback(async (nextValue: UserDTO[]) => {
    setValue(nextValue);
    await updateCollaborators({
      toDoId: toDo?.toDoId ?? 0,
      collaboratorUserIds: getUserIds(nextValue),
    });
  }, [toDo?.toDoId, updateCollaborators]);

  const handleLeave = useCallback(async () => {
    if (isNil(currentUserId)) {
      return;
    }

    const nextValue = collaborators
      .filter((user) => user.userId !== currentUserId);

    setValue(nextValue);
    await updateCollaborators({
      toDoId: toDo?.toDoId ?? 0,
      collaboratorUserIds: getUserIds(nextValue),
    });
  }, [collaborators, currentUserId, toDo?.toDoId, updateCollaborators]);

  return (
    <>
      <CollaboratorsSummary
        collaborators={collaborators}
        showAddButton={showAddButton}
        disabled={toDo?.isAbandoned}
        emptyState={emptyState}
        max={max}
        className={className}
        onClick={handleOpen}
      />
      <Popover
        anchorEl={anchorEl}
        onClose={handleClose}
        classes={{ paper: '!border !border-gray-200 !p-4 !shadow-2xl' }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <div className="flex w-[20rem] flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-gray-500">
                Collaborators
              </span>
              {toDo?.canLeaveFromCollaborators && (
                <Button
                  variant="text"
                  color="inherit"
                  className="!min-w-0 !px-2 !py-1.5 !text-xs !font-semibold !text-amber-700 hover:!bg-amber-50"
                  startIcon={<NotificationIcon className="!h-3.5 !w-3.5" />}
                  onClick={handleLeave}
                  disabled={isUpdating}
                >
                  Leave
                </Button>
              )}
            </div>
            <CollaboratorsAutoComplete
              id={`to-do-collaborators-edit-${toDo?.toDoId ?? 'unknown'}`}
              value={value}
              onChange={handleChange}
              options={activeUsers}
              assigneeUserId={toDo?.assigneeUserId}
              className="!w-full"
              disabled={isUpdating}
            />
          </div>
        </div>
      </Popover>
    </>
  );
}
